import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Footer, Topbar } from '../components/Chrome.tsx';
import { useAuth } from '../lib/auth.tsx';

/** Demo-Konten fuer den Kasten „Zum Ausprobieren" -- angelegt in server/src/domain/seed.ts. */
const DEMO_ACCOUNTS = [
  { email: 'regie@elbufer.de', event: 'Elbufer 10K' },
  { email: 'regie@last-rabbit-standing.test', event: 'Last Rabbit Standing 2027' },
];
const DEMO_PASSWORD = 'rabbit1234';

/** Anmelden und Registrieren in einem Formular. */
export function Login({ mode }: { mode: 'login' | 'register' }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('weiter') ?? '/';
  const { login, register } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (mode === 'register') await register(email, password, displayName);
      else await login(email, password);
      navigate(next, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Hat nicht geklappt.');
    } finally {
      setBusy(false);
    }
  };

  const otherPath = `${mode === 'login' ? '/registrieren' : '/anmelden'}?weiter=${encodeURIComponent(next)}`;

  return (
    <div className="app">
      <Topbar />
      <main className="section">
        <div className="shell shell--narrow">
          <p className="eyebrow">Für Vereine</p>
          <h1 className="page-title">{mode === 'login' ? 'Anmelden' : 'Konto anlegen'}</h1>
          <p className="page-lede">
            Ein Konto brauchst du nur, um Events anzulegen oder Regie zu führen. Zuschauen und als Athlet:in senden
            geht ohne.
          </p>

          <form className="card" onSubmit={submit}>
            <div className="card__body stack gap-16">
              {mode === 'register' && (
                <div className="field">
                  <label htmlFor="name">Name</label>
                  <input
                    id="name"
                    className="input"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Regie Elbufer"
                    autoComplete="name"
                  />
                </div>
              )}
              <div className="field">
                <label htmlFor="email">E-Mail</label>
                <input
                  id="email"
                  className="input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="regie@verein.de"
                  autoComplete="email"
                  required
                />
              </div>
              <div className="field">
                <label htmlFor="pw">Passwort</label>
                <input
                  id="pw"
                  className="input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                  required
                />
                {mode === 'register' && <span className="hint">Mindestens 8 Zeichen.</span>}
              </div>

              {error && <div className="notice">{error}</div>}

              <button className="btn btn--lg btn--block" type="submit" disabled={busy}>
                {busy ? 'Moment …' : mode === 'login' ? 'Anmelden' : 'Konto anlegen'}
              </button>

              <p className="hint" style={{ textAlign: 'center' }}>
                {mode === 'login' ? 'Noch kein Konto? ' : 'Schon ein Konto? '}
                <Link className="linklike" to={otherPath}>
                  {mode === 'login' ? 'Jetzt anlegen' : 'Anmelden'}
                </Link>
              </p>

              {mode === 'login' && (
                <div className="notice notice--info demo-accounts">
                  <p>
                    <b>Zum Ausprobieren</b> — Passwort jeweils <code>{DEMO_PASSWORD}</code>. Antippen setzt die
                    Daten ein:
                  </p>
                  <ul>
                    {DEMO_ACCOUNTS.map((a) => (
                      <li key={a.email}>
                        <button
                          type="button"
                          className="demo-account"
                          onClick={() => {
                            setEmail(a.email);
                            setPassword(DEMO_PASSWORD);
                          }}
                        >
                          <code>{a.email}</code>
                          <span>Admin {a.event}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                  <p>Weitere Demo-Konten stehen im README.</p>
                </div>
              )}
            </div>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
}
